import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, Check, X } from "@phosphor-icons/react"
import { Command as CommandPrimitive } from "cmdk"
import { Button } from "@/components/ui/button"
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command"
import { FormError } from "@/components/ui/form"
import { inputVariant } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useClickOutside } from "@/hooks/useClickOutside"
import { normalizeTestId } from "@/lib/test-helper"
import { cn } from "@/lib/utils"

interface GroupBase<Option> {
  readonly options: readonly Option[]
  readonly heading?: string
}

interface FilterableSelectGroupedProps<Option, Group extends GroupBase<Option>> {
  groups: Group[]
  options?: never
}

interface FilterableSelectUngroupedProps<Option> {
  options: Option[]
  groups?: never
}

interface FilterableSelectCommonProps<Option> {
  label: string
  value?: Option
  error?: string
  clearable?: boolean
  filterable?: boolean
  translateError?: boolean
  innerScrollClassName?: React.HTMLAttributes<HTMLDivElement>["className"]
  "data-testid"?: string
  getOptionLabel: (option: Option) => string
  getOptionValue: (option: Option) => string
  isOptionSelected?: (option: Option, selectedValue: Option) => boolean
  onChange?: (value: Option | undefined) => void
}

type FilterableSelectProps<Option, Group extends GroupBase<Option>> = (
  | (FilterableSelectGroupedProps<Option, Group> & FilterableSelectCommonProps<Option>)
  | (FilterableSelectUngroupedProps<Option> & FilterableSelectCommonProps<Option>)
) &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type">

const FilterableSelect = <Option, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: FilterableSelectProps<Option, Group>,
) => {
  const {
    label,
    value,
    required,
    translateError,
    options,
    groups,
    error,
    clearable,
    filterable = true,
    innerScrollClassName,
    "data-testid": dataTestId,
    className,
    onChange,
    getOptionLabel,
    getOptionValue,
    isOptionSelected,
    ...rest
  } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [internalValue, setInternalValue] = useState<Option | null | undefined>(value)
  const [stringValue, setStringValue] = useState<string>(value ? getOptionLabel(value) : "")
  const { t } = useTranslation()

  useClickOutside((event: MouseEvent) => {
    if (isOpen && resultRef.current && !resultRef.current.contains(event.target as HTMLElement)) {
      setIsOpen(false)
    }
  }, inputRef)

  const _getOptionLabel = useCallback((option: Option) => getOptionLabel(option), [getOptionLabel])
  const _getOptionValue = useCallback((option: Option) => getOptionValue(option), [getOptionValue])
  const _isOptionSelected = (option: Option) =>
    internalValue &&
    (isOptionSelected
      ? isOptionSelected(option, internalValue)
      : _getOptionValue(internalValue) === _getOptionValue(option))

  const handleClear = () => {
    setInternalValue(null)
    setStringValue("")
    onChange?.(undefined)
  }

  const renderOption = (option: Option) => {
    const isSelected = _isOptionSelected(option)
    const label = _getOptionLabel(option)
    const value = _getOptionValue(option)

    return (
      <CommandItem
        key={value}
        data-testid={dataTestId && normalizeTestId(dataTestId, "-item")}
        className={cn(
          "cursor-pointer",
          isSelected && "bg-primary-surface! text-on-primary-surface pointer-events-none",
        )}
        onSelect={() => {
          setInternalValue(option)
          setStringValue(getOptionLabel(option))
          setIsOpen(false)
          onChange?.(option)
        }}>
        {label}
        {isSelected && (
          <span className="absolute right-2 flex h-6 w-6 items-center justify-center">
            <Check className="text-on-primary-surface" />
          </span>
        )}
      </CommandItem>
    )
  }

  const renderGroupedOptions = () => {
    return groups?.map((group, idx) => {
      const key = idx
      return (
        <CommandGroup
          key={key}
          heading={group.heading}
          data-testid={dataTestId && normalizeTestId(dataTestId, "-group")}
          className="p-2">
          {group.options.map((item) => {
            return renderOption(item)
          })}
        </CommandGroup>
      )
    })
  }

  const renderUngroupedOptions = () => {
    return <div className="px-2">{options?.map(renderOption)}</div>
  }

  const renderOptions = () => {
    if (groups) {
      return renderGroupedOptions()
    } else if (options) {
      return renderUngroupedOptions()
    }
  }

  const [shouldFilter, setShouldFilter] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      if (internalValue && stringValue !== _getOptionLabel(internalValue)) {
        setStringValue(_getOptionLabel(internalValue))
      } else if (stringValue.length > 0 && !internalValue) {
        setStringValue("")
      }
    }
  }, [_getOptionLabel, _getOptionValue, internalValue, isOpen, stringValue])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <CommandPrimitive
        className={cn("relative", className)}
        shouldFilter={shouldFilter}
        data-testid={dataTestId && normalizeTestId(dataTestId)}>
        <PopoverTrigger asChild>
          <div className="relative">
            <CommandPrimitive.Input
              placeholder=" "
              {...rest}
              className={cn(
                inputVariant(),
                "focus:placeholder:text-on-surface-subdued disabled:bg-surface-disabled pr-20 placeholder:text-transparent",
                error && "outline-critical! outline-1",
              )}
              value={stringValue}
              data-testid={dataTestId && normalizeTestId(dataTestId, "-input")}
              onValueChange={(search) => {
                setStringValue(search)
                if (filterable) setShouldFilter(true)
                if (!isOpen) setIsOpen(true)
              }}
              ref={inputRef}
              onClick={() => {
                if (!isOpen) setIsOpen(true)
                if (filterable) setShouldFilter(false)
              }}
            />
            <label
              className={cn(
                "text-on-surface-subdued peer-focus:text-primary pointer-events-none absolute top-1 left-4 cursor-text text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs",
                error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
              )}>
              {label}
              {required && "*"}
            </label>
            {clearable && stringValue && (
              <Button
                variant="unstyle"
                data-testid={dataTestId && normalizeTestId(dataTestId, "-clear-button")}
                className="hover:bg-surface-hovered absolute top-6 right-10 -translate-y-1/2 px-2.5"
                onClick={(e) => {
                  e.preventDefault()
                  handleClear()
                }}>
                <X className="text-on-surface-subdued size-5" />
              </Button>
            )}
            <div className="pointer-events-none absolute top-6 right-1 -translate-y-1/2 p-2">
              <CaretDown className="text-on-surface-subdued h-6 w-6" />
            </div>
            {error && <FormError> {translateError ? t(error) : error}</FormError>}
          </div>
        </PopoverTrigger>
        <PopoverContent
          ref={resultRef}
          className="bg-surface shadow-4 z-50 w-(--radix-popper-anchor-width) rounded-xl p-0 outline-hidden"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}>
          <CommandList className="max-h-none px-0">
            <CommandEmpty
              data-testid={dataTestId && normalizeTestId(dataTestId, "-empty")}
              className="px-4 pt-4 pb-0.5 text-sm">
              {t("common.message.noResultFound")}
            </CommandEmpty>
            <ScrollArea>
              <div className={cn("py-2", groups && "divide-y", innerScrollClassName)}>{renderOptions()}</div>
            </ScrollArea>
          </CommandList>
        </PopoverContent>
      </CommandPrimitive>
    </Popover>
  )
}

export { FilterableSelect }
